import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { selectAccountInfoSuccessfullyCompleted, selectAreAccountAssetsPledged, selectHasBundled, selectInfo, selectIsDemoUser, selectIsEmployee, selectIsIip, selectIsPensionTrustAccount, selectMoveMoneyDataAccess, selectUsername } from '../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement, selectHasRequestWire, selectHasUpdateMoneyLink, selectHasUpdateMoveMoney } from '../../authorize/reducers';
import { AdobeConstants } from '../adobe-tagging/AdobeConstants';
import { AdobeHelpers } from '../adobe-tagging/adobeHelper';
import { FeatureFlagService } from '../services/feature-flag-service';
import { transactionDirection, digitalRegTypes } from '../utilities/Constants';

@Component({
  standalone: false,
  selector: 'mm-actions-link',
  templateUrl: './actions-link.component.html',
  styleUrls: ['./actions-link.component.scss'],
})
export class ActionsLinkComponent implements AfterViewInit, OnDestroy {
  params: any;
  showStartTransactionLink: boolean = false;
  isEditableCheck: boolean = false;
  isEditableInternalTransfer: boolean = false;
  isEditableAch: boolean = false;
  isTransactionCancelable: boolean = false;
  isIncomingWire: boolean;
  isDeletedInRTransaction: boolean = false;

  isReactivatableProfile: boolean = false;
  isDeactivatableProfile: boolean = false;
  isDeletableProfile: boolean = false;
  isEditableProfile: boolean = false;
  isTransactionDeactivatableACH: boolean = false;
  isReccuringProfile: boolean = false;

  reactivateAchFlaggedOn$: Observable<boolean>;
  hasMoveMoneyDataAccess: boolean | undefined;
  destroy$ = new Subject<void>();
  isEmployee: boolean;
  isIip: boolean;
  isDemoUser: boolean;
  bundledOfferEnabled: boolean;
  isPensionTrustAccount: boolean;
  isRestrictedFlow$: Observable<boolean>;

  hasUpdateMoveMoneyAccess: boolean;
  hasUpdateMoneyLinkAccess: boolean;
  hasRequestWireAccess: boolean;
  assetsArePledged: boolean;
  isInitiateFsg: boolean;
  isReleaseFsg: boolean;
  userName: string;
  accountInfoFinished$: Observable<boolean | undefined>;
  showViewLink: boolean = true;

  constructor(
    private featureFlagService: FeatureFlagService,
    private store: Store
  ) { }

  agInit(params: any) {
    this.store.select(selectHasUpdateMoveMoney).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.hasUpdateMoveMoneyAccess = val;
    });

    this.store.select(selectHasUpdateMoneyLink).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.hasUpdateMoneyLinkAccess = val;
    });

    this.store.select(selectHasRequestWire).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.hasRequestWireAccess = val;
    });

    this.store.select(selectMoveMoneyDataAccess).pipe(takeUntil(this.destroy$)).subscribe(hasMoveMoneyDataAccess => {
      this.hasMoveMoneyDataAccess = hasMoveMoneyDataAccess;
    });

    this.store.select(selectIsEmployee).subscribe(value => {
      this.isEmployee = value;
    });

    this.store.select(selectIsIip).subscribe(value => {
      this.isIip = value;
    })

    this.store.select(selectIsDemoUser).subscribe(value => {
      this.isDemoUser = value;
    });

    this.store.select(selectHasBundled).pipe(takeUntil(this.destroy$)).subscribe(enabled => this.bundledOfferEnabled = enabled);

    this.store.select(selectIsPensionTrustAccount).pipe(takeUntil(this.destroy$)).subscribe(penTrust => this.isPensionTrustAccount = penTrust);
    
    this.store.select(selectHasAccessInitiateMoneyMovement).pipe(takeUntil(this.destroy$)).subscribe(fsg => {
      this.isInitiateFsg = fsg;
    });

    this.store.select(selectHasAccessReleaseMoneyMovement).pipe(takeUntil(this.destroy$)).subscribe(fsg => {
      this.isReleaseFsg = fsg;
    });

    this.store.select(selectUsername).pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.userName = user;
    });

    this.store.select(selectAreAccountAssetsPledged).pipe(takeUntil(this.destroy$)).subscribe(assetsPledged => {
      this.assetsArePledged = assetsPledged;
    });

    this.params = params;
    const isIip = this.params.data.isIip;

    if (this.params.data.hasRequestTransactionAccess && (!isIip || (isIip && this.params.data.Type?.includes('ACH in'))) && this.IsNotBundledOrIsEditable) {
      this.showStartTransactionLink = true;
    }

    this.accountInfoFinished$ =  this.store.select(selectAccountInfoSuccessfullyCompleted);
    this.accountInfoFinished$.pipe(takeUntil(this.destroy$)).subscribe(finished => {
      if (finished === true) {
        this.isRestrictedFlow$ = of(this.assetsArePledged && this.params.data.isRecurring); //in its own observable due to a race condition
      }
    });

    // Hide create transaction link for all tables when account has D2P registration types
    if (this.params.data.registrationType) {
      const restrictedRegTypes = [...digitalRegTypes];
      if ((restrictedRegTypes.some(regType => this.params.data.registrationType?.includes(regType)) && (this.params.data.Type?.includes('ACH') && this.params.data.isRecurring))) {
        this.showStartTransactionLink = false;
      }
    }

    this.isReactivatableProfile = this.params.data.Type?.includes('ACH') && this.params.data.IsReactivatableProfile;
    this.isDeactivatableProfile = this.params.data.Type?.includes('ACH') && this.params.data.IsDeletableProfile;
    this.isDeletableProfile = !this.params.data.Type?.includes('ACH') && this.params.data.IsDeletableProfile;
    this.isEditableProfile = this.params.data.IsEditableProfile;
    this.isIncomingWire = this.params.data.Type?.includes('Wire') && 
      (this.params.data['Direction']?.includes(transactionDirection.in) || this.params.data['Incoming/Outgoing']?.includes(transactionDirection.in));
    this.isDeletedInRTransaction = (String(this.params.data.Status).toLowerCase() === 'cancelled' || String(this.params.data.Status).toLowerCase() === 'deleted') && this.isInitiateFsg && this.params.data.isDraft;

    const hasCancelAccess = this.getHasCancelAccess();
    this.isTransactionCancelable = this.params.data.IsCancelable && !this.isIip && hasCancelAccess;

    this.isEditableCheck = this.params.data.Type?.includes('Check') && this.params.data.IsEditable && this.hasUpdateMoveMoneyAccess && this.IsNotBundledOrIsEditable;

    this.isEditableInternalTransfer = this.params.data.Type?.includes('Internal transfer') && this.params.data.IsEditable && this.hasUpdateMoveMoneyAccess && this.IsNotBundledOrIsEditable;

    this.isEditableAch = this.params.data.Type?.includes('ACH') && !this.isIip && this.params.data.IsEditable && this.hasUpdateMoneyLinkAccess && !this.isPensionTrustAccount;
    this.reactivateAchFlaggedOn$ = this.featureFlagService.featureFlagSubjects.enableReactivateAchTransactions$.asObservable();

    if (this.params.data.isTemplate) {
      if (this.params.data.hasRequestTransactionAccess && this.IsNotBundledOrIsEditable) {
        this.showStartTransactionLink = true;
      }
      this.isDeletableProfile = true;
    }

    if (this.params.tableName !== undefined &&  this.params.tableName === 'standing authorizations' && this.isInitiateAndReleaseFlow && this.params.data.Type?.includes('ACH') && this.params.data.isRecurring) {
      this.showStartTransactionLink = false;
    }

    const cashieringId = this.params.data.cashieringRqstId || this.params.data.CashieringRqstId;
    const hasCashieringId = !!(cashieringId && cashieringId.trim());
    let isRecurringProfileAndNoCashieringId = this.params.data.IsActiveRecurring && !hasCashieringId;
    this.showViewLink = !this.isIncomingWire && !this.isDeletedInRTransaction && !isRecurringProfileAndNoCashieringId;
  }

  ngAfterViewInit() {
    if (this.params.data.isTemplate && this.showStartTransactionLink) {
      AdobeHelpers.trackClick(`start_transaction_link${this.params.data.DraftId}`, AdobeConstants.ADOBE_ + AdobeConstants._SAVEDTEMPLATE_TAB);
    }
  }

  actionClicked($event: any, name: string) {
    let event = {
      event: $event,
      rowData: this.params.node.data,
      name: name,
    };
    this.params.onClick(event);
  }

  getHasCancelAccess(): boolean {
    if (this.params.data.Type?.includes('ACH')){
      return this.hasUpdateMoneyLinkAccess;
    }
    if (this.params.data.Type?.includes('Wire')) {
      return this.hasRequestWireAccess;
    }
    return this.hasUpdateMoveMoneyAccess;
  }

  get IsNotBundledOrIsEditable(): boolean {
    return this.params.data.isBundled ? this.bundledOfferEnabled : true;
  }

  get viewReviewLink(): string {
    if (this.params.data.isDraft){
      return (this.isReleaseFsg &&  this.params.data['Initiator ID'] !== this.userName) ? this.params.review : this.params.viewStatus;
    }
    return this.params.viewStatus;
  }

  get isCancelableDraft(): boolean {
    return this.isInitiateFsg && this.params.data.isDraft && this.params.data.Status == 'Initiated';
  }

  get cancelDeleteLink(): string {
    return this.params.data.isSloa ? this.params.delete : this.params.cancel;
  }

  get isInitiateAndReleaseFlow(): boolean {
    return this.isInitiateFsg;
  }

  get isCreateRuleTable() {
    return this.params?.tableName === 'createRuleTable';
  }
  get hidePaasbAchStandingAuthEdit(): boolean {
   return this.assetsArePledged && this.params?.data?.Type?.includes('ACH') && (this.params?.data?.isRecurring || this.params?.data?.IsActiveRecurring);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
